/**
 * ADIOSOFT CENTRAL QUOTA NORMALIZATION & ENTITLEMENT PARSER
 * 
 * Strict normalization for database quota values (999, "999", 2, "2", 1, "1", 0, "0", etc.).
 * Guarantees:
 * 1. String & number inputs are safely normalized to non-negative integers.
 * 2. 999 / "999" is treated as real numeric system data (never converted to 2 or 1, nor assumed as test/mock).
 * 3. Invalid types (null, undefined, "", "abc", NaN, negative values, decimals like 2.5) are safely detected and rejected.
 * 4. Prevents implicit string concatenation ("999" - "17" bugs).
 * 5. Reusable across websiteLimit, maxPages, maxYachts, mediaLimit, etc.
 */

export interface NormalizedQuotaResult {
  valid: boolean;
  value: number;
  rawValue: unknown;
  error?: string;
}

export interface QuotaCalculationResult {
  total: number;
  used: number;
  remaining: number;
  valid: boolean;
  error?: string;
  rawValue?: unknown;
}

/**
 * Normalizes an unknown quota input into a verified, non-negative integer.
 * 
 * Rules:
 * - Accepts numbers and trimmed numeric strings (e.g. 999, "999", 2, "2", 0, "0").
 * - Rejects null, undefined, empty strings ("").
 * - Rejects non-numeric strings ("abc", "none").
 * - Rejects booleans, objects, arrays, functions.
 * - Rejects negative numbers (-1, "-5").
 * - Rejects decimal numbers (2.5, "2.5") since quotas represent discrete discrete items.
 * - Rejects NaN and infinite numbers.
 */
export function normalizeQuota(value: unknown): NormalizedQuotaResult {
  // 1. Guard against null or undefined
  if (value === null || value === undefined) {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: 'Limit bulunamadı veya tanımsız (null/undefined).',
    };
  }

  // 2. Reject boolean types (e.g. true or false should not be coerced to 1 or 0)
  if (typeof value === 'boolean') {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: 'Geçersiz veri tipi: boolean kota olarak kullanılamaz.',
    };
  }

  // 3. String handling: trim whitespace and check empty
  let candidate: number;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return {
        valid: false,
        value: 0,
        rawValue: value,
        error: 'Geçersiz kota: boş metin.',
      };
    }

    // Strict numeric check: ensure the whole trimmed string is numeric
    // Rejects hexadecimal (0x...), scientific notation (1e3), or strings with trailing letters
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return {
        valid: false,
        value: 0,
        rawValue: value,
        error: `Geçersiz sayısal format: "${trimmed}".`,
      };
    }

    candidate = Number(trimmed);
  } else if (typeof value === 'number') {
    candidate = value;
  } else {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: 'Desteklenmeyen veri tipi (nesne/dizi).',
    };
  }

  // 4. Check for NaN, Infinite
  if (Number.isNaN(candidate) || !Number.isFinite(candidate)) {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: 'Geçersiz sayı (NaN veya Sonsuz).',
    };
  }

  // 5. Check for Negative Numbers (Requirement 17)
  if (candidate < 0) {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: `Geçersiz kota: negatif değer (${candidate}) kabul edilemez.`,
    };
  }

  // 6. Check for Decimals (Requirement 18)
  if (!Number.isInteger(candidate)) {
    return {
      valid: false,
      value: 0,
      rawValue: value,
      error: `Geçersiz kota: ondalıklı sayı (${candidate}) tam sayı kota olamaz.`,
    };
  }

  // Valid Integer Quota (e.g. 0, 1, 2, 999, 10000)
  return {
    valid: true,
    value: candidate,
    rawValue: value,
  };
}

/**
 * Calculates total, used, and remaining quota from raw inputs.
 * Ensures arithmetic is performed with validated numbers, never string concatenation.
 * 
 * TEST 1: websiteLimit = 2, used = 0       -> remaining = 2
 * TEST 2: websiteLimit = "2", used = 1     -> remaining = 1
 * TEST 3: websiteLimit = 999, used = 3     -> remaining = 996
 * TEST 4: websiteLimit = "999", used = 3   -> remaining = 996
 * TEST 5: websiteLimit = 1, used = 1       -> remaining = 0
 * TEST 6: websiteLimit = "1", used = 1     -> remaining = 0
 * TEST 7: websiteLimit = 0, used = 0       -> remaining = 0
 * TEST 8: websiteLimit = null              -> invalid/missing entitlement
 * TEST 9: websiteLimit = "abc"             -> invalid/missing entitlement
 * TEST 10: websiteLimit = -1               -> invalid entitlement
 */
export function calculateQuotaBreakdown(
  limitInput: unknown,
  usedInput: unknown
): QuotaCalculationResult {
  const normLimit = normalizeQuota(limitInput);
  const normUsed = normalizeQuota(usedInput ?? 0);

  if (!normLimit.valid) {
    return {
      total: 0,
      used: normUsed.valid ? normUsed.value : 0,
      remaining: 0,
      valid: false,
      error: normLimit.error,
      rawValue: limitInput,
    };
  }

  const total = normLimit.value;
  const used = normUsed.valid ? normUsed.value : 0;
  const remaining = Math.max(0, total - used);

  return {
    total,
    used,
    remaining,
    valid: true,
    rawValue: limitInput,
  };
}
